/**
 * General Contractors section (Contacts tab, construction loans).
 *
 * Backend: `generalContractors[]` on the loan model (a GeneralContractor extends
 * ContactBase with UW confirmation + experience fields — general-contractor-docs.md).
 * Add/edit through the generic list-object CRUD:
 *   POST /{prefix}/create_list_object/generalContractors/{dealId}
 *   POST /{prefix}/update_list_object/generalContractors/{dealId}/{_id}
 * Both return the updated loan → dispatch loanDrilldownSet.
 *
 * SHAPE — companies, then the people inside them.
 * A GC record is one PERSON at a firm, and a firm can send more than one (an
 * estimator and the licence holder, say). Flat rows made "Example GC" appear
 * twice with no relationship; the roster now groups by `company`, so the firm
 * is the object and its contractors sit inside it.
 *
 * Each contractor row answers, in order: who they are, what is outstanding
 * ("N to review"), then the three facts underwriting works from — licence,
 * experience, confirmations — and finally the experience items credited to them,
 * folded away because a reviewer opens them one at a time.
 *
 * "N to review" is the open-item count behind the row: an unconfirmed UW toggle
 * (up to three) plus a licence/insurance expiry that is missing, past, or inside
 * thirty days. It is the same arithmetic the chips and the expiry notes show
 * individually — the pill is only the summary, so it can never disagree.
 *
 * LAYOUT — records left, their paperwork right (the Applicants card's split).
 * GeneralContractorDocsPanel is the Documents tab's coverage content for the
 * "General Contractor Docs" group without the collapsible chrome. It is
 * loan-level, like the group itself, so it sits once beside the whole roster
 * rather than repeating per contractor.
 *
 * Top of the left column: the GC qualifying-experience block
 * (`totalVerifiedGCExperience`, HITL — see GcQualifyingExperienceCard), which
 * also carries the Experience Calculator link. The card collapses.
 *
 * This section is a reader only — add and edit both open the shared
 * ADD_LOAN_RELATED_CONTACT sidebar:
 *   add   → { loanRole: "General Contractor" }, which flips that form to the GC
 *           questions inline and writes to generalContractors[]
 *   edit  → { ...gc, _gcRecord: true }, the marker that tells the sidebar this
 *           row is a GC record rather than a loanRelatedContact
 *
 * Legacy rows — a loanRelatedContact whose loanRole is already "General
 * Contractor", created before generalContractors[] existed — are listed below
 * the companies, tagged as legacy, and open their original contact form with
 * the convert-to-GC-record action.
 */
import PropTypes from "prop-types";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import AddRounded from "@mui/icons-material/AddRounded";
import ApartmentRounded from "@mui/icons-material/ApartmentRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import MailOutlineRounded from "@mui/icons-material/MailOutlineRounded";
import RadioButtonUncheckedRounded from "@mui/icons-material/RadioButtonUncheckedRounded";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import WorkspacePremiumOutlined from "@mui/icons-material/WorkspacePremiumOutlined";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import LoanSectionCard from "../../shared/tabLayout/LoanSectionCard";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../../redux/actions/sidebarActions";
import { useDrilldownBaseUrl } from "../../../../pipelines/PipelineContext";
import { detailSidebarClear } from "../../../../redux/reducers/detailSidebarReducer";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { formatPhone } from "../../../../_src_shared/utils/functions/formatPhone";
import { teal } from "../../../../theme/colors";
import { formSx } from "../../../../theme/formStyles";
import ExperienceItemDialog from "../../experience/ExperienceItemDialog";
import ExperienceItemsGrid from "../../experience/ExperienceItemsGrid";
import GcQualifyingExperienceCard from "../../experience/GcQualifyingExperienceCard";
import {
  ASSOCIATION_TYPES,
  fmtPoints,
  itemAddress,
  needsAttention,
  partiesById,
} from "../../experience/experienceConstants";
import { useExperienceWorkbook } from "../../experience/useExperienceWorkbook";
import { CONTACTS_SECTION_ANCHOR_SX } from "./contactsSections";
import { BOX_LINE } from "./components/contactsTokens";
import {
  useCoveragePreview,
  useCoverageUpload,
  useDocGroupCoverage,
} from "../../shared/docGroupCoverage";
import GeneralContractorDocsPanel, {
  CONTRACTOR_DOC_GROUP,
} from "./GeneralContractorDocsPanel";

export const GC_ROLE = "General Contractor";

const INK = "#0f172a";
const INK_2 = "#475569";
const MUTED = "#64748b";
const FAINT = "#94a3b8";
const HAIRLINE = "#eef2f7";
const WARN = "#b45309";
const DANGER = "#b91c1c";
const OK = "#047857";
const OK_FILL = "#ecfdf5";
const OK_LINE = "#a7f3d0";
const WARN_FILL = "#fef3c7";

/** The firm. Falls back to the person so a row is never nameless. */
export const gcName = (g) =>
  g?.company ||
  `${g?.firstName || ""} ${g?.lastName || ""}`.trim() ||
  "General contractor";

/** The PERSON on the record, as distinct from the firm. */
export const gcPersonName = (g) =>
  `${g?.firstName || ""} ${g?.lastName || ""}`.trim();

const gcInitials = (g) => {
  const source = gcPersonName(g) || gcName(g);
  return (
    source
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0])
      .join("")
      .toUpperCase() || "?"
  );
};


const DAY_MS = 86400000;

/**
 * Days until a date-only value, on calendar fields so a negative-offset zone
 * can't shift the day (CLAUDE.md — never parse a date-only string into local
 * time). Negative = already past.
 */
const daysUntil = (value) => {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  const now = new Date();
  const today = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  );
  return Math.round((Date.UTC(y, m - 1, d) - today) / DAY_MS);
};

/**
 * A credential's expiry, aged.
 *
 * GC credentials carry no fixed validity window the way a credit report does —
 * a licence is good until the date printed on it, which the record already
 * stores. So the signal comes from that real date: missing, past, inside thirty
 * days, or current.
 */
const expiryState = (value) => {
  if (!value) return { level: "missing", label: "No expiration on file" };
  const days = daysUntil(value);
  if (days == null) return { level: "missing", label: "No expiration on file" };
  if (days < 0)
    return { level: "expired", label: `Expired ${formatDate(value, "")}` };
  if (days <= 30)
    return {
      level: "soon",
      label: `Expires in ${days} day${days === 1 ? "" : "s"}`,
    };
  return { level: "ok", label: `Expires ${formatDate(value, "")}` };
};

const EXPIRY_COLOR = {
  missing: WARN,
  expired: DANGER,
  soon: WARN,
  ok: FAINT,
};

const ExpiryNote = ({ value }) => {
  const state = expiryState(value);
  const flagged = state.level !== "ok";
  return (
    <Flx ac sx={{ gap: 0.375, mt: 0.25 }}>
      {flagged && (
        <WarningAmberOutlined
          sx={{ fontSize: 13, color: EXPIRY_COLOR[state.level] }}
        />
      )}
      <Txt
        sx={{
          fontSize: 12,
          color: EXPIRY_COLOR[state.level],
          fontWeight: flagged ? 600 : 400,
        }}
      >
        {state.label}
      </Txt>
    </Flx>
  );
};

ExpiryNote.propTypes = { value: PropTypes.any };

/**
 * Everything still open on one contractor, in the reviewer's words.
 *
 * Drives the "N to review" pill. Deliberately derived from the same fields the
 * row already renders — the pill summarizes what is visible rather than
 * introducing a separate judgement.
 */
const reviewItems = (g) => {
  const out = [];
  if (g?.providesCompletionGuaranty !== true)
    out.push("Completion guaranty not confirmed");
  if (g?.licenseConfirmed !== true) out.push("License not confirmed");
  if (g?.insuranceConfirmed !== true) out.push("Insurance not confirmed");
  const lic = expiryState(g?.licenseExpirationDate);
  if (lic.level !== "ok") out.push(`License: ${lic.label.toLowerCase()}`);
  const ins = expiryState(g?.insuranceExpirationDate);
  if (ins.level !== "ok") out.push(`Insurance: ${ins.label.toLowerCase()}`);
  return out;
};

/**
 * A UW confirmation, wired to the document it rests on.
 *
 * The toggle is an attestation — "I read the licence and it is current" — so
 * the chip is the way back to what was read. With a document on file it opens
 * the preview; with none it starts the upload for that exact docType, because
 * "there is nothing to check" is more useful than a dead chip.
 */
const ConfirmChip = ({ ok, label, docs, docTypes, onOpen, onUpload }) => {
  const doc = docs[0] || null;
  const tip = doc
    ? `${ok ? "Confirmed" : "Not confirmed"} · open ${
        doc.file_display_name || doc.name || "the document"
      }${docs.length > 1 ? ` (${docs.length} on file)` : ""}`
    : `${ok ? "Confirmed" : "Not confirmed"} · no ${docTypes[0]} on file — click to upload`;

  return (
    <Tooltip title={tip}>
      <Chip
        size="small"
        clickable
        onClick={() => (doc ? onOpen(doc) : onUpload(docTypes[0]))}
        icon={
          ok ? (
            <CheckRounded sx={{ fontSize: 13 }} />
          ) : (
            <RadioButtonUncheckedRounded sx={{ fontSize: 12 }} />
          )
        }
        label={label}
        variant={ok ? "filled" : "outlined"}
        sx={{
          height: 26,
          fontSize: 12,
          fontWeight: 500,
          borderRadius: "999px",
          bgcolor: ok ? OK_FILL : "transparent",
          borderColor: ok ? OK_LINE : BOX_LINE,
          color: ok ? OK : MUTED,
          "& .MuiChip-icon": { color: "inherit", ml: "6px" },
          "&:hover": { bgcolor: ok ? "#d1fae5" : "#f1f5f9" },
        }}
      />
    </Tooltip>
  );
};

/** Which doc types stand behind each confirmation (General Contractor Docs). */
const CONFIRMATION_DOCS = {
  guaranty: ["Completion Guaranty"],
  license: ["Contractor License"],
  insurance: [
    "Contractor General Liability Insurance",
    "Contractor Workman's Compensation Insurance",
  ],
};

/**
 * A firm, holding its contractors.
 *
 * The body is the grey field the white contractor cards sit ON, so the firm
 * reads as the container and the people as the objects inside it — the same
 * figure/ground the Applicants tab uses. Collapsible because a loan with three
 * firms is otherwise a very long scroll; the header keeps the counts so a
 * closed card still says whether anything is outstanding.
 */
const CompanyCard = ({ name, memberCount, reviewCount, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <Box
      sx={{
        border: `1px solid ${BOX_LINE}`,
        borderRadius: "12px",
        overflow: "hidden",
        background: "#f8fafc",
      }}
    >
      <Flx
        ac
        jb
        g={1}
        component="button"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        sx={{
          width: "100%",
          px: 2,
          py: 1.5,
          background: "transparent",
          border: 0,
          borderBottom: open ? `1px solid ${HAIRLINE}` : "none",
          cursor: "pointer",
          fontFamily: "inherit",
          textAlign: "left",
          "&:hover": { background: "#f1f5f9" },
        }}
      >
        <Flx ac g={1.25} sx={{ minWidth: 0 }}>
          {/* Arrow leads, like every collapsible level on this tab. */}
          <ChevronRightRounded
            sx={{
              fontSize: 20,
              color: FAINT,
              flexShrink: 0,
              transition: "transform .15s",
              transform: open ? "rotate(90deg)" : "none",
            }}
          />
          <Flx
            center
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              border: `1px solid ${BOX_LINE}`,
              background: "#fff",
              color: MUTED,
              flexShrink: 0,
            }}
          >
            <ApartmentRounded sx={{ fontSize: 18 }} />
          </Flx>
          <Box sx={{ minWidth: 0 }}>
            <Txt
              sx={{
                display: "block",
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: ".08em",
                color: FAINT,
              }}
            >
              COMPANY
            </Txt>
            <Txt
              sx={{
                display: "block",
                fontSize: 15,
                fontWeight: 600,
                color: INK,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name}
            </Txt>
          </Box>
        </Flx>
        <Flx ac g={1} sx={{ flexShrink: 0 }}>
          {reviewCount > 0 && (
            <Box
              component="span"
              sx={{
                px: 1,
                py: "2px",
                borderRadius: "999px",
                bgcolor: WARN_FILL,
                color: WARN,
                fontSize: 11.5,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {reviewCount} to review
            </Box>
          )}
          <Txt sx={{ fontSize: 12.5, color: MUTED }}>
            {memberCount} contractor{memberCount === 1 ? "" : "s"}
          </Txt>
        </Flx>
      </Flx>
      <Collapse in={open} unmountOnExit>
        <Flx column sx={{ gap: 1.25, p: 1.25 }}>
          {children}
        </Flx>
      </Collapse>
    </Box>
  );
};

CompanyCard.propTypes = {
  name: PropTypes.string,
  memberCount: PropTypes.number,
  reviewCount: PropTypes.number,
  children: PropTypes.node,
};

/** One column of the licence / experience / confirmations strip. */
const Stat = ({ label, children, divider }) => (
  <Box
    sx={{
      minWidth: 0,
      pl: divider ? 2.5 : 0,
      borderLeft: divider ? `1px solid ${HAIRLINE}` : "none",
    }}
  >
    <Txt
      sx={{
        display: "block",
        fontSize: 12,
        color: MUTED,
        mb: 0.5,
      }}
    >
      {label}
    </Txt>
    {children}
  </Box>
);

Stat.propTypes = {
  label: PropTypes.node,
  children: PropTypes.node,
  divider: PropTypes.bool,
};

/**
 * Credit experience — a split action, because there are two ways to do it and
 * only one of them is "type a new property".
 *
 * A project this contractor worked on is very often ALREADY on the loan,
 * entered against a borrower or pulled from a track record. Making "new item"
 * the only affordance invites a duplicate row for the same address, which then
 * has to be found and removed. The default stays the new item; the second way
 * is one click behind the caret.
 */
const CreditExperienceButton = ({ firstName, onNewItem, onCreditExisting }) => {
  const [anchor, setAnchor] = useState(null);
  const close = () => setAnchor(null);
  return (
    <>
      <ButtonGroup
        size="small"
        variant="outlined"
        color="gray"
        sx={{ flexShrink: 0, background: "#fff" }}
      >
        <Button
          startIcon={<AddRounded sx={{ fontSize: "16px !important" }} />}
          onClick={onNewItem}
          sx={{
            textTransform: "none",
            fontSize: 12.5,
            fontWeight: 600,
            minHeight: 32,
            px: 1.5,
          }}
        >
          Credit experience
        </Button>
        <Button
          aria-label="More ways to credit experience"
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ minWidth: 32, px: 0 }}
        >
          <KeyboardArrowDownRounded sx={{ fontSize: 18 }} />
        </Button>
      </ButtonGroup>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              minWidth: 260,
              borderRadius: "10px",
              border: `1px solid ${BOX_LINE}`,
              py: 0.5,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            close();
            onNewItem();
          }}
          sx={{ display: "block", px: 2, py: 1.1 }}
        >
          <Txt sx={{ display: "block", fontSize: 13.5, fontWeight: 600, color: INK }}>
            New experience item
          </Txt>
          <Txt sx={{ display: "block", fontSize: 12, color: MUTED }}>
            Enter a property {firstName} worked on
          </Txt>
        </MenuItem>
        <MenuItem
          onClick={() => {
            close();
            onCreditExisting();
          }}
          sx={{ display: "block", px: 2, py: 1.1 }}
        >
          <Txt sx={{ display: "block", fontSize: 13.5, fontWeight: 600, color: INK }}>
            Credit an existing item
          </Txt>
          <Txt sx={{ display: "block", fontSize: 12, color: MUTED }}>
            Pick from this project&apos;s experience
          </Txt>
        </MenuItem>
      </Menu>
    </>
  );
};

CreditExperienceButton.propTypes = {
  firstName: PropTypes.string,
  onNewItem: PropTypes.func,
  onCreditExisting: PropTypes.func,
};

/**
 * Nothing credited yet.
 *
 * The GC's OWN stated project count is the useful thing to say here: the
 * profile claims N, the loan counts none of them, and the gap between those two
 * numbers is exactly the reviewer's job. Neutral pronouns — a name doesn't tell
 * us anyone's.
 */
const EmptyCreditedExperience = ({ firstName, stated, action }) => (
  <Flx
    ac
    jb
    g={2}
    sx={{
      border: `1px dashed ${BOX_LINE}`,
      borderRadius: "10px",
      px: 2,
      py: 1.75,
      flexWrap: "wrap",
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Txt
        sx={{ display: "block", fontSize: 13.5, fontWeight: 600, color: INK }}
      >
        Nothing credited to {firstName} yet
      </Txt>
      <Txt sx={{ display: "block", fontSize: 12.5, color: MUTED, mt: 0.25 }}>
        {stated > 0
          ? `${firstName} has ${stated} unverified project${
              stated === 1 ? "" : "s"
            } on their profile. Credit one to count it toward this loan.`
          : `No projects are stated on ${firstName}'s profile. Credit an experience item to count one toward this loan.`}
      </Txt>
    </Box>
    {/* The prompt and the way to act on it are one object — the button belongs
        inside the box it is asking about, not floating above it. */}
    {action}
  </Flx>
);

EmptyCreditedExperience.propTypes = {
  firstName: PropTypes.string,
  stated: PropTypes.number,
  action: PropTypes.node,
};

/**
 * Pick an experience item already on the loan and credit it to this
 * contractor — `associations.generalContractors` gains the GC, nothing is
 * duplicated, and the rollup attributes it on the next verify.
 */
const CreditExistingDialog = ({
  open,
  onClose,
  items,
  firstName,
  busy,
  onPick,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ fontSize: 16, fontWeight: 600, pb: 1 }}>
      Credit an existing item to {firstName}
    </DialogTitle>
    <DialogContent dividers sx={{ px: 0, py: 0 }}>
      {items.length === 0 ? (
        <Txt sx={{ display: "block", fontSize: 12.5, color: MUTED, p: 3 }}>
          Every experience item on this loan already credits {firstName}. Use
          <strong> New experience item</strong> to enter another property.
        </Txt>
      ) : (
        items.map((it) => (
          <Box
            key={it._id}
            component="button"
            type="button"
            disabled={!!busy}
            onClick={() => onPick(it)}
            sx={{
              display: "block",
              width: "100%",
              textAlign: "left",
              background: "none",
              border: 0,
              borderBottom: `1px solid ${HAIRLINE}`,
              px: 3,
              py: 1.5,
              cursor: busy ? "default" : "pointer",
              fontFamily: "inherit",
              "&:last-of-type": { borderBottom: "none" },
              "&:hover": { background: "#f8fafc" },
            }}
          >
            <Flx ac jb g={1}>
              <Box sx={{ minWidth: 0 }}>
                <Txt
                  sx={{
                    display: "block",
                    fontSize: 13,
                    fontWeight: 600,
                    color: INK,
                  }}
                >
                  {itemAddress(it)}
                </Txt>
                <Txt sx={{ display: "block", fontSize: 12, color: MUTED }}>
                  {[it.experienceType, it.resolutionStatus]
                    .filter(Boolean)
                    .join(" · ") || "No type or resolution set"}
                </Txt>
              </Box>
              <Chip
                size="small"
                label={it.verified ? "Verified" : "Candidate"}
                sx={{
                  flexShrink: 0,
                  height: 20,
                  fontSize: 11,
                  fontWeight: 600,
                  bgcolor: it.verified ? OK_FILL : "#eef2f7",
                  color: it.verified ? OK : MUTED,
                }}
              />
            </Flx>
          </Box>
        ))
      )}
    </DialogContent>
    <DialogActions sx={{ px: 3, py: 1.5 }}>
      <Button onClick={onClose} sx={{ textTransform: "none" }}>
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

CreditExistingDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  items: PropTypes.array,
  firstName: PropTypes.string,
  busy: PropTypes.any,
  onPick: PropTypes.func,
};

/**
 * The experience items crediting ONE contractor, folded away.
 *
 * The `X of Y projects verified` figure above states the number; this is what
 * it is made of. Rows open the SAME dialog the Experience Calc. ledger opens —
 * it is that ledger's grid, filtered — so proof, credits and the verify toggle
 * are here rather than a tab away.
 *
 * Starts collapsed either way — the header line already says how many items
 * and how many are verified, which is what a reviewer scanning the roster
 * needs; the table and the empty-state prompt are one click in.
 */
const CreditedExperience = ({
  items,
  loan,
  partyById,
  activeItemId,
  onOpenItem,
  onNewItem,
  onCreditExisting,
  firstName,
  stated,
}) => {
  const [open, setOpen] = useState(false);
  const verified = items.filter((it) => it?.verified).length;
  const flagged = items.some((it) => needsAttention(it));

  // One button, rendered in whichever place the expanded body ends up using.
  const creditAction = (
    <CreditExperienceButton
      firstName={firstName}
      onNewItem={onNewItem}
      onCreditExisting={onCreditExisting}
    />
  );

  return (
    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${HAIRLINE}` }}>
      <Flx ac jb g={1} sx={{ flexWrap: "wrap" }}>
        <Flx
          ac
          g={0.75}
          component="button"
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          sx={{
            background: "none",
            border: 0,
            p: 0,
            cursor: "pointer",
            fontFamily: "inherit",
            textAlign: "left",
            minWidth: 0,
          }}
        >
          <ChevronRightRounded
            sx={{
              fontSize: 18,
              color: FAINT,
              transition: "transform .15s",
              transform: open ? "rotate(90deg)" : "none",
            }}
          />
          <Txt sx={{ fontSize: 13, fontWeight: 600, color: INK }}>
            Credited experience
          </Txt>
          <Txt sx={{ fontSize: 12, color: MUTED }}>
            {items.length
              ? `${items.length} item${items.length === 1 ? "" : "s"} · ${verified} verified`
              : "0 items"}
          </Txt>
          {flagged && (
            <Tooltip title="An item still has no parent or no proof — it can't persist until that is fixed.">
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: "#e08a1e",
                  flexShrink: 0,
                }}
              />
            </Tooltip>
          )}
        </Flx>
      </Flx>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 1.25 }}>
          {items.length === 0 ? (
            <EmptyCreditedExperience
              firstName={firstName}
              stated={stated}
              action={creditAction}
            />
          ) : (
            <>
              <ExperienceItemsGrid
                items={items}
                loan={loan}
                byId={partyById}
                activeItemId={activeItemId}
                onRowClick={onOpenItem}
              />
              {/* Under the table, on the action side — the list is the subject,
                  adding to it is what you do after reading it. */}
              <Flx jc="flex-end" sx={{ mt: 1.25 }}>
                {creditAction}
              </Flx>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

CreditedExperience.propTypes = {
  items: PropTypes.array,
  loan: PropTypes.object,
  partyById: PropTypes.instanceOf(Map),
  activeItemId: PropTypes.string,
  onOpenItem: PropTypes.func,
  onNewItem: PropTypes.func,
  onCreditExisting: PropTypes.func,
  firstName: PropTypes.string,
  stated: PropTypes.number,
};

/* ──────────────────────────── the section ──────────────────────────────── */

const GeneralContractorsSection = ({ id }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // The item dialog reads the loan's parties and documents, so it needs the
  // whole deal rather than the slices this card renders.
  const loan = useSelector((s) => s?.loanDrilldown);
  const dealId = loan?._id;
  const gcs = loan?.generalContractors;
  const contacts = loan?.loanRelatedContacts;
  const baseUrl = useDrilldownBaseUrl(dealId);

  // The same coverage model the docs panel on the right renders, reused here so
  // each confirmation chip can reach the document it attests to.
  const { types } = useDocGroupCoverage(CONTRACTOR_DOC_GROUP);
  const { uploadTo, inputRef, onInputChange } = useCoverageUpload();
  const openPreview = useCoveragePreview();

  /** docType → its files, newest first. */
  const docsByType = useMemo(() => {
    const map = new Map();
    types.forEach((t) =>
      map.set(
        t.name,
        [...t.files].sort((a, b) =>
          String(b?.uploadDate || "").localeCompare(String(a?.uploadDate || "")),
        ),
      ),
    );
    return map;
  }, [types]);

  const docsFor = useCallback(
    (docTypes) => docTypes.flatMap((t) => docsByType.get(t) || []),
    [docsByType],
  );

  const list = useMemo(
    () => (Array.isArray(gcs) ? gcs.filter(Boolean) : []),
    [gcs],
  );

  /** One entry per firm, in the order the firms first appear on the loan. */
  const companies = useMemo(() => {
    const order = [];
    const byKey = new Map();
    list.forEach((g) => {
      const name = (g?.company || "").trim();
      const key = name.toLowerCase() || `__person__${g?._id}`;
      if (!byKey.has(key)) {
        byKey.set(key, { key, name: name || "No company on file", members: [] });
        order.push(key);
      }
      byKey.get(key).members.push(g);
    });
    return order.map((k) => byKey.get(k));
  }, [list]);

  const legacy = useMemo(
    () =>
      (Array.isArray(contacts) ? contacts : []).filter(
        (c) => c && c.loanRole === GC_ROLE,
      ),
    [contacts],
  );

  // The workbook, shared with the Experience Calc. tab through
  // `loanFetchedData.experience` — one copy, so opening an item here and
  // verifying it updates that ledger too. The hook loads on first mount.
  const wb = useExperienceWorkbook(dealId);
  const experienceApplies = wb.experienceApplies !== false;

  /** gcId → the items crediting that contractor. */
  const itemsByGc = useMemo(() => {
    const map = new Map();
    (wb.items || []).forEach((it) =>
      (it?.associations?.generalContractors || []).forEach((gid) => {
        const key = String(gid);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(it);
      }),
    );
    return map;
  }, [wb.items]);

  // The ledger resolves each item's credits against the loan's parties.
  const partyById = useMemo(() => partiesById(loan), [loan]);

  // `null` = closed. An item with an `_id` opens in edit mode; a bare
  // `{ associations }` opens create mode with the GC pre-credited.
  const [experienceItem, setExperienceItem] = useState(null);
  // The contractor whose "credit an existing item" picker is open.
  const [creditingFor, setCreditingFor] = useState(null);

  /** Items on the loan that do NOT already credit the contractor. */
  const creditableFor = useCallback(
    (gcId) =>
      (wb.items || []).filter(
        (it) =>
          !(it?.associations?.generalContractors || [])
            .map(String)
            .includes(String(gcId)),
      ),
    [wb.items],
  );

  /** Add this GC to an existing item's credits — no duplicate row. */
  const creditExisting = useCallback(
    async (item) => {
      if (!creditingFor?._id || !item?._id) return;
      await wb.addAssociation(
        item._id,
        ASSOCIATION_TYPES.GENERAL_CONTRACTORS,
        String(creditingFor._id),
      );
      setCreditingFor(null);
    },
    [creditingFor, wb],
  );

  // Which contractors are expanded. Every contractor starts collapsed: the
  // header row already carries the name, contact line and "N to review".
  const [expandedGcs, setExpandedGcs] = useState(() => new Set());
  const toggleGc = useCallback((gid) => {
    setExpandedGcs((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      return next;
    });
  }, []);

  // Highlight whichever row is open in the shared add/edit sidebar.
  const editingId = useSelector((state) => {
    if (
      state.sidebar?.type !== loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT
    )
      return null;
    return state.sidebar?.state?._id || null;
  });

  const openSidebar = useCallback(
    (state) => {
      dispatch(detailSidebarClear());
      dispatch(
        sidebarTypeSet(loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT),
      );
      dispatch(sidebarSetStateValue(state));
    },
    [dispatch],
  );

  // No `_id` → create mode; the role prefill is what swaps the form over to the
  // GC questions.
  const openAdd = useCallback(
    () => openSidebar({ loanRole: GC_ROLE }),
    [openSidebar],
  );
  const openEdit = useCallback(
    (g) => openSidebar({ ...g, _gcRecord: true }),
    [openSidebar],
  );
  const openLegacy = useCallback((c) => openSidebar({ ...c }), [openSidebar]);

  const isEmpty = list.length === 0 && legacy.length === 0;
  const gcCount = list.length + legacy.length;

  // Files in the General Contractor Docs group, de-duped by id in case one file
  // is filed under more than one type — the header says how much paperwork the
  // card holds even while it's collapsed.
  const docCount = useMemo(() => {
    const ids = new Set();
    let anon = 0;
    types.forEach((t) =>
      (t.files || []).forEach((f) => {
        if (f?._id) ids.add(String(f._id));
        else anon += 1;
      }),
    );
    return ids.size + anon;
  }, [types]);
  const headerMeta = [
    list.length > 0 &&
      `${companies.length} compan${companies.length === 1 ? "y" : "ies"}`,
    `${docCount} document${docCount === 1 ? "" : "s"}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <LoanSectionCard
      id={id}
      sx={CONTACTS_SECTION_ANCHOR_SX}
      title={
        // Same n pill as Key roles / All contacts, beside the title.
        <Flx ac component="span" sx={{ gap: 1 }}>
          General contractors
          <Box
            component="span"
            aria-label={`${gcCount} general contractor${gcCount === 1 ? "" : "s"}`}
            sx={formSx.sectionCount(false)}
          >
            {gcCount}
          </Box>
        </Flx>
      }
      subTitle="Contractors on this project, grouped by company."
      count={headerMeta}
      collapsible
      chevronStart
      // Nothing to show but the docs rail with no contractors — start folded.
      defaultOpen={!isEmpty}
      controls={
        <Button
          size="small"
          variant="contained"
          disableElevation
          startIcon={<AddRounded sx={{ fontSize: "17px !important" }} />}
          onClick={openAdd}
          sx={{ textTransform: "none" }}
        >
          Add General Contractor
        </Button>
      }
    >
      <Box
        sx={{
          display: "grid",
          // Everything about the GCs left, the doc group right — the doc-type
          // names are long ("Contractor Workman's Compensation Insurance"), so
          // the rail gets real width. One column until there is room for both.
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 420px" },
          alignItems: "start",
          gap: 1.5,
        }}
      >
        <Flx column sx={{ gap: 1.5, minWidth: 0 }}>
          {/* The loan-root GC verified total — proposed by the experience
              workbook, applied here (HITL). Heads the GC column as a plain
              block, so it reads as the roster's summary rather than one more
              firm. The Experience Calculator link lives on it: that tab is
              where the number is credited. With no contractors there is
              nothing to credit, so neither the link nor (absent a proposal or
              an applied value) the block renders. */}
          <GcQualifyingExperienceCard
            borderColor={BOX_LINE}
            action={
              list.length > 0 ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="gray"
                  startIcon={
                    <WorkspacePremiumOutlined
                      sx={{ fontSize: "17px !important" }}
                    />
                  }
                  onClick={() => navigate(`${baseUrl}/experience`)}
                  sx={{ textTransform: "none", background: "#fff" }}
                >
                  Experience Calculator
                </Button>
              ) : null
            }
          />

          <Box sx={{ minWidth: 0 }}>
            {isEmpty ? (
              <Txt sx={{ fontSize: 12.5, color: FAINT, py: 1 }}>
                No general contractors on this loan yet.
              </Txt>
            ) : (
              <Flx column sx={{ gap: 1.5 }}>
                {companies.map((company) => (
                  <CompanyCard
                    key={company.key}
                    name={company.name}
                    memberCount={company.members.length}
                    reviewCount={company.members.reduce(
                      (n, m) => n + reviewItems(m).length,
                      0,
                    )}
                  >
                      {company.members.map((g) => {
                        const open = reviewItems(g);
                        const person = gcPersonName(g) || gcName(g);
                        const items = itemsByGc.get(String(g._id)) || [];
                        const verified = fmtPoints(g.verifiedExperienceTotal);
                        const stated =
                          g.projectsCompleted != null
                            ? g.projectsCompleted
                            : null;
                        const hasVerified =
                          Number(g.verifiedExperienceTotal) > 0;
                        const gid = String(g._id);
                        const expanded = expandedGcs.has(gid);
                        return (
                          <Box
                            key={g._id}
                            sx={{
                              border: "1px solid",
                              borderColor:
                                g._id === editingId
                                  ? "primary.main"
                                  : BOX_LINE,
                              borderRadius: "10px",
                              p: 2,
                              // White on the company's grey field — the person
                              // is the object, the firm is the container.
                              background: "#fff",
                            }}
                          >
                            {/* who — the row toggles the detail below; the
                                chevron is the keyboard-reachable control */}
                            <Flx
                              jb
                              g={1}
                              onClick={() => toggleGc(gid)}
                              sx={{ alignItems: "center", cursor: "pointer" }}
                            >
                              <Flx ac g={1.25} sx={{ minWidth: 0 }}>
                                <IconButton
                                  size="small"
                                  aria-expanded={expanded}
                                  aria-label={`${
                                    expanded ? "Collapse" : "Expand"
                                  } ${person}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleGc(gid);
                                  }}
                                  sx={{ ml: -0.75, flexShrink: 0 }}
                                >
                                  <ChevronRightRounded
                                    sx={{
                                      fontSize: 20,
                                      color: FAINT,
                                      transition: "transform .15s",
                                      transform: expanded
                                        ? "rotate(90deg)"
                                        : "none",
                                    }}
                                  />
                                </IconButton>
                                <Flx
                                  center
                                  sx={{
                                    flexShrink: 0,
                                    width: 40,
                                    height: 40,
                                    borderRadius: "999px",
                                    bgcolor: teal[50],
                                    color: teal[700],
                                    fontSize: 12.5,
                                    fontWeight: 700,
                                  }}
                                >
                                  {gcInitials(g)}
                                </Flx>
                                <Box sx={{ minWidth: 0 }}>
                                  <Flx ac g={0.75} sx={{ flexWrap: "wrap" }}>
                                    <Txt
                                      sx={{
                                        fontSize: 15,
                                        fontWeight: 600,
                                        letterSpacing: "-0.01em",
                                        color: INK,
                                      }}
                                    >
                                      {person}
                                    </Txt>
                                    {open.length > 0 && (
                                      <Tooltip
                                        title={
                                          <Box
                                            component="ul"
                                            sx={{ m: 0, pl: 2 }}
                                          >
                                            {open.map((o) => (
                                              <li key={o}>{o}</li>
                                            ))}
                                          </Box>
                                        }
                                      >
                                        <Box
                                          component="span"
                                          sx={{
                                            px: 1,
                                            py: "2px",
                                            borderRadius: "999px",
                                            bgcolor: WARN_FILL,
                                            color: WARN,
                                            fontSize: 11.5,
                                            fontWeight: 600,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {open.length} to review
                                        </Box>
                                      </Tooltip>
                                    )}
                                  </Flx>
                                  <Txt
                                    sx={{
                                      display: "block",
                                      fontSize: 12.5,
                                      color: MUTED,
                                      fontVariantNumeric: "tabular-nums",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {[
                                      g.phone ? formatPhone(g.phone) : null,
                                      g.emailAddress,
                                    ]
                                      .filter(Boolean)
                                      .join(" · ") ||
                                      "No phone or email on file"}
                                  </Txt>
                                </Box>
                              </Flx>
                              <Flx
                                ac
                                onClick={(e) => e.stopPropagation()}
                                sx={{ gap: 0.25, flexShrink: 0 }}
                              >
                                {g.emailAddress && (
                                  <Tooltip title={`Email ${person}`}>
                                    <IconButton
                                      size="small"
                                      component="a"
                                      href={`mailto:${g.emailAddress}`}
                                      aria-label={`Email ${person}`}
                                    >
                                      <MailOutlineRounded
                                        sx={{ fontSize: 17 }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title={`Edit ${person}`}>
                                  <IconButton
                                    size="small"
                                    aria-label={`Edit ${person}`}
                                    onClick={() => openEdit(g)}
                                  >
                                    <EditOutlined sx={{ fontSize: 17 }} />
                                  </IconButton>
                                </Tooltip>
                              </Flx>
                            </Flx>

                            <Collapse in={expanded} unmountOnExit>
                            {/* the three facts underwriting works from */}
                            <Box
                              sx={{
                                mt: 1.5,
                                pt: 1.5,
                                borderTop: `1px solid ${HAIRLINE}`,
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "1fr",
                                  sm: "minmax(0, 1fr) minmax(0, 1fr)",
                                  md: "minmax(0, 0.8fr) minmax(0, 1fr) minmax(0, 1.3fr)",
                                },
                                gap: 2,
                              }}
                            >
                              <Stat label="License">
                                <Txt
                                  sx={{
                                    display: "block",
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: g.licenseNumber ? INK : FAINT,
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  {g.licenseNumber || "Not on file"}
                                </Txt>
                                <ExpiryNote value={g.licenseExpirationDate} />
                              </Stat>

                              <Stat label="Experience">
                                <Txt
                                  sx={{
                                    display: "block",
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: hasVerified ? OK : INK,
                                  }}
                                >
                                  {stated == null
                                    ? `${verified} verified`
                                    : `${verified} of ${stated} projects verified`}
                                </Txt>
                                <Txt
                                  sx={{
                                    display: "block",
                                    fontSize: 12,
                                    color: MUTED,
                                    mt: 0.25,
                                  }}
                                >
                                  {g.yearsExperience != null
                                    ? `${g.yearsExperience} yrs in business`
                                    : "Years in business not stated"}
                                </Txt>
                              </Stat>

                              <Stat label="UW confirmations" divider>
                                <Flx ac wrap sx={{ gap: 0.75 }}>
                                  <ConfirmChip
                                    ok={!!g.providesCompletionGuaranty}
                                    label="Completion guaranty"
                                    docTypes={CONFIRMATION_DOCS.guaranty}
                                    docs={docsFor(CONFIRMATION_DOCS.guaranty)}
                                    onOpen={openPreview}
                                    onUpload={(t) =>
                                      uploadTo(CONTRACTOR_DOC_GROUP, t)
                                    }
                                  />
                                  <ConfirmChip
                                    ok={!!g.licenseConfirmed}
                                    label="License"
                                    docTypes={CONFIRMATION_DOCS.license}
                                    docs={docsFor(CONFIRMATION_DOCS.license)}
                                    onOpen={openPreview}
                                    onUpload={(t) =>
                                      uploadTo(CONTRACTOR_DOC_GROUP, t)
                                    }
                                  />
                                  <ConfirmChip
                                    ok={!!g.insuranceConfirmed}
                                    label="Insurance"
                                    docTypes={CONFIRMATION_DOCS.insurance}
                                    docs={docsFor(CONFIRMATION_DOCS.insurance)}
                                    onOpen={openPreview}
                                    onUpload={(t) =>
                                      uploadTo(CONTRACTOR_DOC_GROUP, t)
                                    }
                                  />
                                </Flx>
                              </Stat>
                            </Box>

                            {/* what the verified figure is made of */}
                            {experienceApplies && (
                              <CreditedExperience
                                items={items}
                                loan={loan}
                                partyById={partyById}
                                activeItemId={
                                  experienceItem?._id
                                    ? String(experienceItem._id)
                                    : undefined
                                }
                                firstName={g.firstName || person}
                                stated={
                                  g.projectsCompleted != null
                                    ? Number(g.projectsCompleted)
                                    : 0
                                }
                                onOpenItem={(it) => setExperienceItem(it)}
                                onNewItem={() =>
                                  setExperienceItem({
                                    associations: {
                                      generalContractors: [String(g._id)],
                                    },
                                  })
                                }
                                onCreditExisting={() => setCreditingFor(g)}
                              />
                            )}

                            {g.experienceNotes && (
                              <Flx
                                g={1}
                                sx={{
                                  mt: 1.5,
                                  alignItems: "baseline",
                                  minWidth: 0,
                                }}
                              >
                                <Txt
                                  sx={{
                                    fontSize: 11.5,
                                    color: FAINT,
                                    flexShrink: 0,
                                  }}
                                >
                                  Note
                                </Txt>
                                <Txt sx={{ fontSize: 12.5, color: INK_2 }}>
                                  {g.experienceNotes}
                                </Txt>
                              </Flx>
                            )}
                            </Collapse>
                          </Box>
                        );
                      })}
                  </CompanyCard>
                ))}

                {/* Pre-generalContractors[] rows: still real, just on the old
                    model — no company grouping to put them in. */}
                {legacy.map((c) => (
                  <Box
                    key={c._id}
                    sx={{
                      border: "1px dashed",
                      borderColor:
                        c._id === editingId ? "primary.main" : BOX_LINE,
                      borderRadius: "10px",
                      p: 1.75,
                      background: "#fbfcfe",
                    }}
                  >
                    <Flx jb ac wrap sx={{ gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Flx ac wrap sx={{ gap: 0.75 }}>
                          <Txt
                            sx={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: INK,
                            }}
                          >
                            {gcName(c)}
                          </Txt>
                          <Chip
                            size="small"
                            label="Legacy contact"
                            sx={{
                              height: 20,
                              fontSize: 10.5,
                              fontWeight: 600,
                              bgcolor: "#eef2f7",
                              color: MUTED,
                            }}
                          />
                        </Flx>
                        <Txt sx={{ fontSize: 12, color: MUTED }}>
                          Stored as a loan contact — open it to convert to a
                          General Contractor record.
                        </Txt>
                      </Box>
                      <IconButton
                        size="small"
                        aria-label={`Edit ${gcName(c)}`}
                        onClick={() => openLegacy(c)}
                      >
                        <EditOutlined sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Flx>
                  </Box>
                ))}
              </Flx>
            )}
          </Box>
        </Flx>

        <GeneralContractorDocsPanel title="General contractor docs" />
      </Box>

      {/* One picker for the chips' "nothing on file — upload it" path; the
          docs panel keeps its own. */}
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={onInputChange}
      />

      {/* The Experience Calc. ledger's own dialog, mounted here so a GC's items
          are editable and verifiable without leaving Contacts. It writes through
          the same workbook hook, so both surfaces stay one copy. `onExtract` is
          left off deliberately — the extract flow belongs to the tab that owns
          the document picker. */}
      <ExperienceItemDialog
        open={!!experienceItem}
        onClose={() => setExperienceItem(null)}
        item={experienceItem}
        loan={loan}
        busy={wb.busy}
        onCreate={wb.createItem}
        onUpdate={wb.updateItem}
        onVerify={wb.verifyItem}
        onRemove={wb.removeItem}
      />

      {/* Credit a property already on the loan to this contractor, rather than
          entering a second row for the same address. */}
      <CreditExistingDialog
        open={!!creditingFor}
        onClose={() => setCreditingFor(null)}
        items={creditingFor ? creditableFor(creditingFor._id) : []}
        firstName={
          creditingFor
            ? creditingFor.firstName || gcPersonName(creditingFor) || "this GC"
            : ""
        }
        busy={wb.busy}
        onPick={creditExisting}
      />
    </LoanSectionCard>
  );
};

GeneralContractorsSection.propTypes = { id: PropTypes.string };

export default GeneralContractorsSection;
